import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Button, Card, CardContent, Chip, Container, Divider, Skeleton, Stack, Typography } from '@mui/material';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';

import quotesApi from '../../api/quotesApi';
import { getBlobErrorMessage, getErrorMessage } from '../../utils/errorHandler';
import { triggerBlobDownload } from '../../utils/downloadFile';

const currencyPen = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' });
const currencyUsd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const formatDate = (value) => (value ? new Date(value).toLocaleDateString('es-PE') : '—');

const STATUS_LABELS = { borrador: 'Borrador', enviada: 'Enviada', convertida: 'Convertida en venta' };
const STATUS_COLORS = { borrador: 'default', enviada: 'info', convertida: 'success' };

// "Mis cotizaciones": las cotizaciones que el cliente armo en el cotizador de PC con su sesion
// iniciada, con su PDF.
export default function QuoteHistoryPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    quotesApi
      .getMyQuotes({ per_page: 50 })
      .then(({ data }) => setQuotes(data.data))
      .catch((error) =>
        enqueueSnackbar(getErrorMessage(error, 'No se pudieron cargar tus cotizaciones.'), { variant: 'error' })
      )
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  const handleDownload = async (quote) => {
    setDownloadingId(quote.id);
    try {
      const response = await quotesApi.downloadGuestQuotePdf(quote.uuid);
      triggerBlobDownload(response.data, `cotizacion-${quote.id}.pdf`);
    } catch (error) {
      enqueueSnackbar(await getBlobErrorMessage(error, 'No se pudo descargar la cotización.'), { variant: 'error' });
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <Stack spacing={1} sx={{ mb: 4 }}>
        <Typography variant="h4">Mis cotizaciones</Typography>
        <Typography color="text.secondary">Las cotizaciones que armaste en el cotizador de PC.</Typography>
      </Stack>

      {loading && <Skeleton variant="rounded" height={160} />}

      {!loading && quotes.length === 0 && (
        <Card>
          <CardContent>
            <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center', py: 4 }}>
              <RequestQuoteOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
              <Typography variant="h6">Aún no tienes cotizaciones</Typography>
              <Button variant="contained" component={RouterLink} to="/cotizador">
                Armar mi PC
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Stack spacing={2}>
        {quotes.map((quote) => (
          <Card key={quote.id}>
            <CardContent>
              <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                <Stack>
                  <Typography variant="subtitle1">Cotización N.° {quote.id}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(quote.created_at)} · {quote.items?.length || 0} producto(s)
                  </Typography>
                </Stack>
                <Chip
                  size="small"
                  label={STATUS_LABELS[quote.status?.name] || quote.status?.name || '—'}
                  color={STATUS_COLORS[quote.status?.name] || 'default'}
                />
              </Stack>

              <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                {(quote.items || []).map((item) => (
                  <Typography key={item.id} variant="body2">
                    {item.quantity} × {item.product?.name || item.description || 'Producto'}
                  </Typography>
                ))}
              </Stack>

              <Divider sx={{ mb: 1.5 }} />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
              >
                <Typography variant="subtitle2">
                  Total: {currencyPen.format(quote.subtotal_pen)} / {currencyUsd.format(quote.subtotal_usd)}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PictureAsPdfOutlinedIcon />}
                  onClick={() => handleDownload(quote)}
                  disabled={downloadingId === quote.id}
                >
                  {downloadingId === quote.id ? 'Descargando...' : 'Descargar PDF'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Container>
  );
}
